import {
  Users, Kanban, Settings,
  BarChart2, ListTodo, FileText, Megaphone, FileSignature,
  CalendarDays, BookMarked, Palette, Compass,
  Lightbulb, Brain, Share2, Sparkles, Sun,
  HardHat, Wallet, Magnet, GraduationCap,
  Timer, Radar, ClipboardList,
} from 'lucide-react'

export interface NavItem {
  to: string
  label: string
  icon: React.ComponentType<{ className?: string }>
}

export interface NavGroup {
  /** Stable id — the localStorage key for this group's open/closed memory. */
  id: string
  label: string
  items: NavItem[]
  /** Whether the group starts open the first time (before any saved state). */
  defaultOpen?: boolean
}

/**
 * Pinned core — always visible at the top, above every collapsible group.
 * Chat is not a route; it opens the assistant sidebar and is rendered separately.
 */
export const CORE_ITEMS: NavItem[] = [
  { to: '/today', label: 'Today', icon: Sun },
  { to: '/inbox', label: 'Inbox', icon: Sparkles },
  { to: '/pipeline', label: 'Pipeline', icon: Kanban },
  { to: '/contacts', label: 'Contacts', icon: Users },
  { to: '/tasks', label: 'Tasks', icon: ListTodo },
]

/**
 * Collapsible workflow groups. Every non-core, non-Mantastic route lives in
 * exactly one group so nothing becomes unreachable. The old "Intelligence"
 * junk drawer is split into Signals (live, actionable) and Reference (static).
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    id: 'work',
    label: 'Work',
    defaultOpen: true,
    items: [
      { to: '/proposals', label: 'Proposals', icon: FileText },
      { to: '/delivery', label: 'Projects', icon: HardHat },
      { to: '/timesheet', label: 'Timesheet', icon: Timer },
      { to: '/academy', label: 'Academy', icon: GraduationCap },
      { to: '/calendar', label: 'Calendar', icon: CalendarDays },
      { to: '/contracts', label: 'Contracts & Authorities', icon: FileSignature },
    ],
  },
  {
    id: 'money',
    label: 'Money',
    items: [
      { to: '/finance', label: 'Finance', icon: Wallet },
      { to: '/reports', label: 'Reports', icon: BarChart2 },
    ],
  },
  {
    id: 'grow',
    label: 'Grow',
    items: [
      { to: '/ideas', label: 'Ideas', icon: Lightbulb },
      { to: '/campaigns', label: 'Campaigns', icon: Megaphone },
      { to: '/lead-magnets', label: 'Lead magnets', icon: Magnet },
      { to: '/content', label: 'Content', icon: Share2 },
    ],
  },
  {
    id: 'library',
    label: 'Library',
    items: [
      { to: '/memories', label: 'Memories', icon: Brain },
      { to: '/reference', label: 'Reference', icon: BookMarked },
      { to: '/brand', label: 'Brand', icon: Palette },
      { to: '/settings', label: 'Settings', icon: Settings },
    ],
  },
]

/**
 * Mantastic is Todd's personal CEO operating system — a single top-level entry
 * (its eight pages are tabs) rendered only for the owner. Kept out of the
 * groups and out of ALL_NAV_ITEMS so it is gated at every nav surface.
 */
export const CEO_WEEKLY_ITEM: NavItem = { to: '/ceo-weekly-planning', label: 'CEO Weekly Planning', icon: ClipboardList }
export const MANTASTIC_ITEM: NavItem = { to: '/mantastic', label: 'Mantastic', icon: Compass }
export const AUTHORITY_ITEM: NavItem = { to: '/authority', label: 'Authority', icon: Radar }

/** Every routable nav item across core + groups. Used to prove route coverage. */
export const ALL_NAV_ITEMS: NavItem[] = [
  ...CORE_ITEMS,
  ...NAV_GROUPS.flatMap((g) => g.items),
]
